import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Target } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { asArray } from "@/lib/utils";
import type { SessionPerformanceStats } from "@shared/schema";

type PerfRow = SessionPerformanceStats;

function corridorPct(rows: PerfRow[]): number | null {
  const balls = rows.reduce((s, r) => s + (r.ballsBowled ?? 0), 0);
  const inCorridor = rows.reduce((s, r) =>
    s + (r.bowlOffStump ?? 0) + (r.bowlMiddleStump ?? 0) + (r.bowlLegStump ?? 0), 0);
  return balls > 0 ? Math.round((inCorridor / balls) * 100) : null;
}

function goodLengthPct(rows: PerfRow[]): number | null {
  const balls = rows.reduce((s, r) => s + (r.ballsBowled ?? 0), 0);
  const gl = rows.reduce((s, r) => s + (r.bowlGoodLength ?? 0) + (r.bowlFull ?? 0), 0);
  return balls > 0 ? Math.round((gl / balls) * 100) : null;
}

function ratio(taken: number, attempted: number): number | null {
  return attempted > 0 ? Math.round((taken / attempted) * 100) : null;
}

function scoreColor(pct: number): string {
  if (pct >= 70) return "bg-green-100 text-green-700";
  if (pct >= 50) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-600";
}

function MetricChip({ label, value }: { label: string; value: number | null }) {
  if (value === null) return null;
  return (
    <div className={`rounded-xl px-4 py-3 text-center ${scoreColor(value)}`}>
      <p className="text-2xl font-bold">{value}%</p>
      <p className="text-xs mt-0.5 font-medium opacity-80">{label}</p>
    </div>
  );
}

interface Props {
  kidId: number;
}

export function KidPerformanceTab({ kidId }: Props) {
  const { data, isLoading } = useQuery<PerfRow[]>({
    queryKey: ["/api/performance-stats/player", kidId],
    queryFn: async () => {
      const r = await apiRequest("GET", `/api/performance-stats/player/${kidId}?limit=10`);
      const json = await r.json();
      return asArray<PerfRow>(json.data);
    },
  });

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-gray-400">Loading…</div>;
  }

  const rows = asArray<PerfRow>(data);
  const captureUrl = `/coach/players/${kidId}/performance-capture`;

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="rounded-full bg-gray-100 p-4">
          <Target className="h-8 w-8 text-gray-400" />
        </div>
        <div>
          <p className="font-semibold text-gray-700">No performance data yet</p>
          <p className="text-sm text-gray-500 mt-1">Start tracking delivery zones and shot data.</p>
        </div>
        <Link
          to={captureUrl}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
        >
          Track first session
        </Link>
      </div>
    );
  }

  const corridor = corridorPct(rows);
  const goodLen = goodLengthPct(rows);

  const totalCatchAttempted = rows.reduce((s, r) => s + (r.fieldCatchesAttempted ?? 0), 0);
  const totalCatchTaken = rows.reduce((s, r) => s + (r.fieldCatchesTaken ?? 0), 0);
  const totalGroundBalls = rows.reduce((s, r) => s + (r.fieldGroundBalls ?? 0), 0);
  const totalGroundClean = rows.reduce((s, r) => s + (r.fieldGroundClean ?? 0), 0);
  const totalThrowsAttempted = rows.reduce((s, r) => s + (r.fieldThrowsAttempted ?? 0), 0);
  const totalThrowsOnTarget = rows.reduce((s, r) => s + (r.fieldThrowsOnTarget ?? 0), 0);

  const catchPct = ratio(totalCatchTaken, totalCatchAttempted);
  const groundPct = ratio(totalGroundClean, totalGroundBalls);
  const throwPct = ratio(totalThrowsOnTarget, totalThrowsAttempted);

  const latest = rows[0];
  const hasFitness = latest && (
    latest.fitYoyoLevel != null || latest.fitSprint20mSec != null || latest.fitPushUps != null
  );

  return (
    <div className="space-y-6 py-2">
      {(corridor !== null || goodLen !== null) && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Bowling</h3>
          <div className="grid grid-cols-2 gap-3">
            <MetricChip label="Corridor %" value={corridor} />
            <MetricChip label="Good length %" value={goodLen} />
          </div>
        </section>
      )}

      {(catchPct !== null || groundPct !== null || throwPct !== null) && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fielding</h3>
          <div className="grid grid-cols-3 gap-2">
            <MetricChip label="Catch %" value={catchPct} />
            <MetricChip label="Ground clean %" value={groundPct} />
            <MetricChip label="Throw accuracy %" value={throwPct} />
          </div>
        </section>
      )}

      {hasFitness && (
        <section>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Fitness (latest)</h3>
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex flex-wrap gap-4 text-sm">
            {latest.fitYoyoLevel != null && (
              <span><span className="text-gray-500">Yo-Yo</span> <span className="font-semibold">{latest.fitYoyoLevel}</span></span>
            )}
            {latest.fitSprint20mSec != null && (
              <span><span className="text-gray-500">Sprint</span> <span className="font-semibold">{latest.fitSprint20mSec}s</span></span>
            )}
            {latest.fitPushUps != null && (
              <span><span className="text-gray-500">Push-ups</span> <span className="font-semibold">{latest.fitPushUps}</span></span>
            )}
          </div>
        </section>
      )}

      <div className="pt-2">
        <Link
          to={captureUrl}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          Track new session →
        </Link>
      </div>
    </div>
  );
}
